import { useMemo } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import { fg } from '@confluence/feature-gating';
import {
	type ContentExtensionMetrics,
	retrieveContentExtensionMetricsFromNode,
} from '@confluence/view-content-perf-metrics/entry-points/extensionAnalyticsHelpers.ts';

import {
	createLcmNestingLevelTracker,
	traverseWithNestedContentAndDepth,
} from './traverseWithNestedContent.ts';

export type AdfStats = {
	contentExtensionMetrics: ContentExtensionMetrics;
	lcmMaxNestingLevel?: number;
	nodeCountByType: Record<string, number>;
	totalNodeCount: number;
};

type CustomPerfData =
	| {
			contentExtensionMetrics?: ContentExtensionMetrics;
			lcmMaxNestingLevel?: number;
	  }
	| null
	| undefined;

export function useAdfStats(
	adfDocument: DocNode,
	customPerfData?: CustomPerfData,
	shouldCollectLcmMaxNestingLevel = true,
): AdfStats {
	const shouldRetrieveContentExtensionMetrics = Boolean(customPerfData);
	const shouldRetrieveLcmMaxNestingLevel =
		shouldRetrieveContentExtensionMetrics && shouldCollectLcmMaxNestingLevel;

	const adfStats = useMemo(() => {
		const contentExtensionMetrics: ContentExtensionMetrics = {
			contentExtensionCount: {
				connect: {},
				forge: {},
			},
			contentExtensionKeys: {
				connect: [],
				forge: [],
			},
			depth: {
				connect: [],
				forge: [],
			},
			hasExtensions: false,
		};
		const nodeCountByType: Record<string, number> = {};
		let totalNodeCount = 0;
		let lcmMaxNestingLevel: number | undefined;
		const recordNode = (node: ADFEntity, depth?: number) => {
			nodeCountByType[node.type] = (nodeCountByType[node.type] || 0) + 1;
			totalNodeCount++;

			if (shouldRetrieveContentExtensionMetrics) {
				retrieveContentExtensionMetricsFromNode(node, contentExtensionMetrics, depth);
			}
		};

		if (shouldRetrieveLcmMaxNestingLevel && fg('confluence_lcm_nesting_telemetry')) {
			const lcmNestingLevelTracker = createLcmNestingLevelTracker();
			traverseWithNestedContentAndDepth(adfDocument, (node, depth, lcmNestedContentLevel) => {
				recordNode(node, depth);
				lcmNestingLevelTracker.visit(lcmNestedContentLevel);
			});
			lcmMaxNestingLevel = lcmNestingLevelTracker.getMaxNestingLevel();
		} else {
			traverseWithNestedContentAndDepth(adfDocument, recordNode);
		}

		return {
			contentExtensionMetrics,
			lcmMaxNestingLevel,
			nodeCountByType,
			totalNodeCount,
		};
	}, [adfDocument, shouldRetrieveContentExtensionMetrics, shouldRetrieveLcmMaxNestingLevel]);

	if (shouldRetrieveContentExtensionMetrics) {
		customPerfData!.contentExtensionMetrics = adfStats.contentExtensionMetrics;
		if (adfStats.lcmMaxNestingLevel !== undefined) {
			customPerfData!.lcmMaxNestingLevel = adfStats.lcmMaxNestingLevel;
		}
	}

	return adfStats;
}
